/**
 * Temu 鞋类尺码 ↔ 脚长映射（对齐 bg.glo.goods.sizecharts.foot.get / 官方文档）。
 */
import staticFootMap from '@/temu-platform/data/temuShoeSizeFootMap.json';

/** 鞋类尺码表 classId（平台「尺码表分类」） */
export const TEMU_SHOE_SIZE_CLASS_IDS = new Set([11, 12, 13, 14, 107]);

export const TEMU_SHOE_SIZE_GROUPS = [
  { key: 'euSize', labels: ['eu', '欧码', 'european', 'eu size'] },
  { key: 'ukSize', labels: ['uk', '英码', 'uk size', 'british'] },
  { key: 'usSize', labels: ['us', '美码', 'us size', 'american'] },
  { key: 'brSize', labels: ['br', '巴西', 'brazil'] },
  { key: 'mxSize', labels: ['mx', '墨西哥', 'mexico'] },
  { key: 'jpSize', labels: ['jp', '日本', 'japan'] },
  { key: 'krSize', labels: ['kr', '韩国', 'korea'] },
  { key: 'clSize', labels: ['cl', '智利', 'chile'] },
  { key: 'coSize', labels: ['co', '哥伦比亚', 'colombia'] },
  { key: 'footLength', labels: ['foot', '脚长', 'footlength', 'sole'] },
];

/**
 * @param {unknown} classId
 * @returns {boolean}
 */
export function isTemuShoeSizeClass(classId) {
  return TEMU_SHOE_SIZE_CLASS_IDS.has(Number(classId));
}

/**
 * classId → foot.get 列表字段名
 * @param {unknown} classId
 * @param {boolean} isDoubleSize
 */
export function shoeFootMapKeyForClass(classId, isDoubleSize = false) {
  const id = Number(classId);
  const dual = Boolean(isDoubleSize);
  switch (id) {
    case 11:
      return dual ? 'menFineDoubleSize' : 'menFineSize';
    case 12:
    case 14: // 通用「鞋子」默认按女鞋映射
      return dual ? 'womenFineDoubleSize' : 'womenFineSize';
    case 13:
      return dual ? 'girlsFineDoubleSize' : 'girlsFineSize';
    case 107:
      return dual ? 'boysFineDoubleSize' : 'boysFineSize';
    default:
      return dual ? 'womenFineDoubleSize' : 'womenFineSize';
  }
}

/**
 * @param {Record<string, unknown>|null|undefined} footMap
 * @param {unknown} classId
 * @param {boolean} isDoubleSize
 * @returns {Array<Record<string, unknown>>}
 */
export function listShoeFootRows(footMap, classId, isDoubleSize = false) {
  const key = shoeFootMapKeyForClass(classId, isDoubleSize);
  const src = footMap && typeof footMap === 'object' ? footMap : staticFootMap;
  const rows = src?.[key];
  return Array.isArray(rows) ? rows : [];
}

export function getStaticShoeFootMap() {
  return staticFootMap;
}

/** 将 element 名称映射到脚长码表字段 */
export function matchShoeFieldKey(elementName = '') {
  const n = String(elementName || '').trim().toLowerCase();
  if (!n) return '';
  for (const g of TEMU_SHOE_SIZE_GROUPS) {
    if (g.labels.some((lab) => n.includes(String(lab).toLowerCase()))) {
      return g.key;
    }
  }
  return '';
}

function normSizeToken(v) {
  return String(v || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/infant/g, '');
}

/**
 * 用已选尺码名在映射表中找行（优先欧码，其次其它码制）。
 * @param {Array<Record<string, unknown>>} rows
 * @param {string} sizeLabel
 */
export function findShoeFootRow(rows, sizeLabel) {
  const want = normSizeToken(sizeLabel);
  if (!want || !Array.isArray(rows)) return null;
  const keys = ['euSize', 'usSize', 'ukSize', 'jpSize', 'krSize', 'brSize', 'mxSize', 'clSize', 'coSize'];
  for (const key of keys) {
    const hit = rows.find((row) => normSizeToken(row?.[key]) === want);
    if (hit) return hit;
  }
  // 双码区间：标签落在 "36-37" 或与区间一端相等
  for (const row of rows) {
    const eu = String(row?.euSize || '').trim();
    if (!eu.includes('-')) continue;
    if (normSizeToken(eu) === want) return row;
    const [a, b] = eu.split('-').map((x) => normSizeToken(x));
    if (want === a || want === b) return row;
  }
  return null;
}

/**
 * 按平台 meta 列把脚长映射填进 records.values。
 * @returns {Array<{ values: Record<string, string> }>}
 */
export function buildShoeSizeChartRecords({
  sizeLabels = [],
  footRows = [],
  groupList = [],
  elementList = [],
} = {}) {
  const groupId = String(groupList[0]?.id ?? 1);
  const elements = Array.isArray(elementList) ? elementList : [];
  const labels = (sizeLabels || []).map((n) => String(n || '').trim()).filter(Boolean);
  return labels.map((label) => {
    const values = { [groupId]: label };
    const row = findShoeFootRow(footRows, label);
    elements.forEach((el) => {
      const id = String(el?.id ?? '');
      if (!id || id === groupId) return;
      const field = matchShoeFieldKey(el?.name || el?.groupChName || '');
      if (!field || !row) return;
      let raw = row[field];
      if (field === 'footLength' && raw != null && typeof raw === 'object') {
        // footLengthRange → 取中点或 min
        const min = Number(raw.min);
        const max = Number(raw.max);
        if (Number.isFinite(min) && Number.isFinite(max)) raw = Math.round((min + max) / 2);
        else if (Number.isFinite(min)) raw = min;
        else raw = '';
      }
      if (raw == null || raw === '') return;
      values[id] = String(raw);
    });
    return { values };
  });
}
